/**
 * Agronomic reference tables.
 *
 * These are typical published values (FAO-56, USDA NRCS, and irrigation-design
 * references) suitable for residential scheduling. They are intentionally
 * data-driven and documented so they can be tuned per install. They are not
 * precise for every microclimate — the soil-balance model self-corrects over
 * time from actual ET0 and rainfall.
 */
/** Inches of depth one gallon of water makes over one ft² (231 in³/gal ÷ 144 in²/ft²). */
export const IN_PER_GAL_PER_SQFT = 1.604;
/**
 * Coarse drip-layout presets. Each seeds the two quantities the drip water model
 * needs (see docs/specs/drip-water-model.md): a wetted fraction `f` (fraction of
 * the plant's canopy the emitters wet) and an effective precipitation rate over
 * the canopy (in/hr). These let a user pick one item instead of measuring
 * emitter count, flow, and canopy area; a measured layout overrides them via
 * {@link derivedDripPrecipRate}. Values are typical irrigation-design starting
 * points for emitter spacing/flow, in the spirit of the other tables here — not
 * exact for every install; a linked sensor closes the gap.
 */
export const DRIP_LAYOUT_PRESETS = {
    INLINE_DRIPLINE: { wettedFraction: 0.7, precipRateInPerHr: 0.4 }, // grid tubing, dense beds/groundcover
    POINT_DENSE: { wettedFraction: 0.4, precipRateInPerHr: 0.35 }, // shrub rows, closely spaced emitters
    POINT_SPARSE: { wettedFraction: 0.25, precipRateInPerHr: 0.3 }, // trees, 1–few emitters per plant
    MICRO_SPRAY: { wettedFraction: 0.8, precipRateInPerHr: 0.8 }, // micro-spray / bubbler, wide footprint
};
/**
 * Mid-season crop coefficient (Kc). Crop ET = ET0 × Kc.
 * Turf/landscape values approximate WUCOLS / FAO-56.
 */
export const CROP_COEFFICIENT = {
    COOL_SEASON_GRASS: 0.8,
    WARM_SEASON_GRASS: 0.65,
    SHRUBS: 0.5,
    TREES: 0.55,
    XERISCAPE: 0.3,
    ANNUALS: 0.8,
    PERENNIALS: 0.6,
    VEGETABLE_GARDEN: 0.9,
};
/**
 * Plant-available water capacity, inches of water per inch of soil depth.
 * (Difference between field capacity and permanent wilting point.)
 */
export const AVAILABLE_WATER_CAPACITY_IN_PER_IN = {
    SAND: 0.05,
    LOAMY_SAND: 0.07,
    SANDY_LOAM: 0.1,
    LOAM: 0.17,
    SILT_LOAM: 0.2,
    CLAY_LOAM: 0.18,
    CLAY: 0.17,
};
/**
 * Maximum depth (inches) that can be applied in one pass before runoff begins,
 * on flat ground. Lower for fine soils that infiltrate slowly. Combined with
 * {@link SLOPE_RUNOFF_FACTOR} to drive cycle-and-soak.
 */
export const MAX_APPLICATION_PER_CYCLE_IN = {
    SAND: 0.5,
    LOAMY_SAND: 0.45,
    SANDY_LOAM: 0.4,
    LOAM: 0.35,
    SILT_LOAM: 0.3,
    CLAY_LOAM: 0.25,
    CLAY: 0.15,
};
/**
 * Approximate time (hours) for a saturated soil to drain by gravity down to
 * field capacity — the width of the plateau-search window after a wetting
 * event (see field-capacity.ts). Coarse soils drain in a few hours; fine soils
 * hold water and take a day or more. Values follow typical published gravity-
 * drainage-to-FC times (USDA NRCS drainage guidance / irrigation-design
 * references); like the rest of this file they are starting points, not exact.
 */
export const DRAINAGE_HOURS = {
    SAND: 6,
    LOAMY_SAND: 8,
    SANDY_LOAM: 12,
    LOAM: 18,
    SILT_LOAM: 24,
    CLAY_LOAM: 30,
    CLAY: 36,
};
/**
 * How long (hours) after a run to keep waiting for a zone's moisture sensor to
 * rise before concluding the run didn't reach it. The wetting front reaches a
 * root-depth probe quickly in coarse soils but lags for hours in fine ones, so
 * finalizing "no rise" on a fixed short window false-flags slow soils that are
 * watering fine. Roughly tracks (but is shorter than) drainage time — we only
 * need the probe to START responding, not fully drain to field capacity — and
 * is capped so a genuinely dry run is still surfaced the same day. See
 * evaluateSensorResponse in sensor-response.ts.
 */
export const SENSOR_RESPONSE_WAIT_HOURS = {
    SAND: 3,
    LOAMY_SAND: 3,
    SANDY_LOAM: 4,
    LOAM: 5,
    SILT_LOAM: 7,
    CLAY_LOAM: 9,
    CLAY: 12,
};
/** Slope reduces how much water can soak in before running off. */
export const SLOPE_RUNOFF_FACTOR = {
    FLAT: 1.0,
    SLIGHT: 0.8,
    MODERATE: 0.6,
    STEEP: 0.4,
};
/** Shade reduces evapotranspiration relative to full sun. */
export const SUN_EXPOSURE_FACTOR = {
    FULL_SUN: 1.0,
    PARTIAL_SHADE: 0.8,
    FULL_SHADE: 0.6,
};
/** Default application rate (inches/hour) by nozzle type, if unmeasured. */
export const DEFAULT_PRECIP_RATE_IN_PER_HR = {
    FIXED_SPRAY: 1.5,
    ROTOR: 0.5,
    ROTARY_NOZZLE: 0.6,
    DRIP: 0.3,
    BUBBLER: 2.0,
};
/**
 * Management allowed depletion (MAD) by plant type — the fraction of
 * plant-available water a zone may lose before it is watered. This is the
 * single "how dry before watering" knob: it drives the ET estimate directly,
 * and the soil-sensor "water at" bound is derived from it (see
 * {@link moistureBoundsPct}), so the two agree whether or not a sensor is
 * linked.
 *
 * Water-sensitive, shallow-rooted plants (flowers, vegetables) use a lower
 * fraction — watered more often and more gently; drought-tolerant plantings
 * use a higher one. Values follow FAO-56 depletion fractions (p) and WUCOLS.
 */
export const ALLOWED_DEPLETION_BY_PLANT = {
    COOL_SEASON_GRASS: 0.5,
    WARM_SEASON_GRASS: 0.5,
    SHRUBS: 0.5,
    TREES: 0.5,
    XERISCAPE: 0.6,
    ANNUALS: 0.4, // flower beds — sensitive, watered more often
    PERENNIALS: 0.4,
    VEGETABLE_GARDEN: 0.35,
};
/**
 * Volumetric water content (%) at field capacity and permanent wilting point
 * by soil type. Consistent with {@link AVAILABLE_WATER_CAPACITY_IN_PER_IN}
 * (FC − PWP ≈ AWC × 100). Typical published FC/PWP figures (USDA NRCS); real
 * sensors vary by placement and calibration, so these are starting points.
 */
export const FIELD_CAPACITY_PCT = {
    SAND: 10,
    LOAMY_SAND: 13,
    SANDY_LOAM: 18,
    LOAM: 27,
    SILT_LOAM: 31,
    CLAY_LOAM: 35,
    CLAY: 37,
};
export const WILTING_POINT_PCT = {
    SAND: 5,
    LOAMY_SAND: 6,
    SANDY_LOAM: 8,
    LOAM: 10,
    SILT_LOAM: 11,
    CLAY_LOAM: 17,
    CLAY: 20,
};
/**
 * Volumetric water content (%) at SATURATION — total porosity, the physical
 * ceiling a reading can reach when every pore is full. Always well above
 * {@link FIELD_CAPACITY_PCT}: the gap between them is the gravitational water
 * that drains off in the hours after heavy rain. Typical published total-
 * porosity figures (USDA NRCS); coarse soils pack denser (lower porosity), fine
 * soils hold more.
 *
 * Used as a physical guard on calibration: a drainage "plateau" at or above
 * saturation is still holding gravitational water (ponding / a soupy, slow-
 * draining event), so it cannot be field capacity and must never anchor the
 * calibrated "full" (see reachedFieldCapacity in calibration.ts). This is the
 * absolute backstop; the temporal settle-gate in field-capacity.ts catches the
 * more common sub-saturation shoulder.
 */
export const SATURATION_PCT = {
    SAND: 40,
    LOAMY_SAND: 42,
    SANDY_LOAM: 43,
    LOAM: 47,
    SILT_LOAM: 48,
    CLAY_LOAM: 48,
    CLAY: 50,
};
/**
 * Default soil-moisture-sensor calibration bounds (volumetric water content,
 * %) for a zone. `highPct` = field capacity ("full"); `lowPct` = the reading
 * at the plant's allowed depletion ("water at") — the sensor-space expression
 * of the exact same threshold {@link ALLOWED_DEPLETION_BY_PLANT} applies to
 * the ET estimate. So a clay flower bed (MAD 0.4) waters at ≈ 30%, whether by
 * estimate or by sensor. Editable per zone.
 */
export function moistureBoundsPct(soil, plant) {
    const fc = FIELD_CAPACITY_PCT[soil];
    const availablePct = fc - WILTING_POINT_PCT[soil];
    const lowPct = Math.round(fc - ALLOWED_DEPLETION_BY_PLANT[plant] * availablePct);
    return { lowPct, highPct: fc };
}
//# sourceMappingURL=tables.js.map